import { DividerText } from "@/components/atoms/DividerText";
import { Indeterminate } from "@/components/atoms/Indeterminate";
import { NoItems } from "@/components/atoms/NoItems";
import { Title } from "@/components/atoms/Title";
import { EventCard } from "@/components/events/EventCard";
import { PageHeader } from "@/components/molecules/PageHeader";
import { MultiSelect } from "@/components/organisms/MultiSelect";
import { Button } from "@/components/ui/button";
import { useEventByYear } from "@/lib/api/event";
import { useOrganizerByYear } from "@/lib/api/organizer";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { useYear } from "@/lib/hooks/useYear";
import { Link } from "@tanstack/react-router";
import { isAfter, isBefore } from "date-fns";
import { useState } from "react";

export function EventsOverview() {
  const { year } = useYear()

  const { data: events, isLoading: isLoadingEvents } = useEventByYear(year);

  const { data: organizers, isLoading: isLoadingOrganizers } = useOrganizerByYear(year);
  const [selectedOrganizers, setSelectedOrganizers] = useState<string[]>([]);

  const isMobile = useIsMobile();

  if (isLoadingEvents || isLoadingOrganizers) {
    return <Indeterminate />;
  }

  const handleValueChange = (value: string[]) => setSelectedOrganizers(value);

  const now = Date.now();
  const filteredEvents = selectedOrganizers.length ? events?.filter(event => event.organizers.find(({ id }) => selectedOrganizers.includes(id.toString()))) : events;
  const futureEvents = filteredEvents?.filter(event => isAfter(event.endTime ?? event.startTime, now)) ?? [];
  const pastEvents = filteredEvents?.filter(event => isBefore(event.endTime ?? event.startTime, now)).sort((a, b) => b.startTime.getTime() - a.startTime.getTime()) ?? [];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader>
        <Title>{`${!isMobile ? "Events " : ""} ${year.formatted}`}</Title>
        <Button size="lg" variant="outline" disabled={!events?.length} asChild>
          <Link to="/events/assign" params={{ year: year.formatted }}>
            Assign
          </Link>
        </Button>
      </PageHeader>
      <div className="flex justify-between gap-6">
        <MultiSelect
          options={organizers?.map(({ id, name }) => ({ value: id.toString(), label: name })) ?? []}
          onValueChange={handleValueChange}
          defaultValue={selectedOrganizers}
          placeholder="Filter by organizer"
          maxCount={2}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {futureEvents.map(event => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
      {pastEvents.length > 0 && (
        <>
          <DividerText>
            Past Events
          </DividerText>
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
            {pastEvents.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </>
      )}
      {events?.length === 0 && <NoItems title="No events found" description="Get started by adding events to the Zeus WPI website" />}
    </div>
  );
}
